/* eslint-disable max-lines-per-function */
import { Checkbox } from '@atlaskit/checkbox';
import { CheckboxField } from '@atlaskit/form';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { uploadStaticAssets } from '@atlassian/mpac-api-client';
import { ImageField, SelectInput, TextInput } from '@atlassian/mpac-ui';
import React from 'react';

import { SLIDE_TYPES, SLIDES_TYPE_OPTIONS } from './HPBannerSlideConfig.constants';
import SpotlightWithImageRightSlideConfig from './SlideConfigComponents/SpotlightWithImageRightSlideConfig';

import type { MultiStepInitialContent } from '@atlassian/mpac-partners';
import type { BannerConfigurationType } from '@atlassian/mpac-types';
import type { ImageUrlType } from '@atlassian/mpac-ui';

const flexStyles = xcss({
  gap: 'space.300',
  flexDirection: 'column',
});

const inputBoxStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'space.400',
});

const imageBoxStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'space.300',
});

const checkboxStyles = xcss({
  display: 'flex',
  alignItems: 'center',
});

type Props = {
  value: MultiStepInitialContent & BannerConfigurationType & { screenshot?: ImageUrlType };
  onFieldChange: (
    fieldName: string,
    id: string
  ) => (newValue: string | ImageUrlType | boolean) => void;
  isDisabled?: boolean;
};

const HPBannerSlideConfig = (props: Props) => {
  const { value, onFieldChange, isDisabled } = props;

  const handleImageFieldChange = (fieldName: string, id: string) => (e: { imageUrl?: string }) => {
    onFieldChange(fieldName, id)(e.imageUrl || '');
  };

  const getImageFieldUrl = (imageUrl?: string) => (imageUrl ? { imageUrl } : {});

  return (
    <Flex xcss={flexStyles}>
      <Box xcss={inputBoxStyles}>
        <SelectInput
          name={`type_${value.id}`}
          label="Select Slide Type"
          defaultValue={value.type ? [SLIDES_TYPE_OPTIONS[value.type]] : []}
          isRequired
          placeholder="Select Slide Type"
          options={Object.values(SLIDES_TYPE_OPTIONS)}
          onChange={(e) => {
            onFieldChange('type', value.id)(e.value);
          }}
        />
      </Box>
      {value?.type === SLIDE_TYPES.SPOTLIGHT_WITH_RIGHT_IMAGE && (
        <SpotlightWithImageRightSlideConfig
          value={value}
          onFieldChange={onFieldChange}
          isDisabled={isDisabled}
        />
      )}
      {value?.type && value.type !== SLIDE_TYPES.SPOTLIGHT_WITH_RIGHT_IMAGE && (
        <Box xcss={inputBoxStyles}>
          {value?.type === SLIDE_TYPES.HERO_3_COLUMN_LAYOUT && (
            <>
              <TextInput
                label="Heading"
                placeholder="Heading for the banner"
                isRequired
                defaultValue={value.heading}
                name={`heading_${value.id}`}
                onChange={onFieldChange('heading', value.id)}
                applyDefaultValidation
              />
              <TextInput
                label="Sub Heading"
                placeholder="Sub Heading for the banner"
                isRequired
                defaultValue={value.subHeading}
                name={`subHeading_${value.id}`}
                onChange={onFieldChange('subHeading', value.id)}
                applyDefaultValidation
              />
            </>
          )}

          <TextInput
            label="Redirection Link"
            placeholder="Redirection URL for click on image"
            defaultValue={value.redirectUrl}
            name={`redirectUrl_${value.id}`}
            onChange={onFieldChange('redirectUrl', value.id)}
            applyDefaultValidation
          />
          {value?.type === SLIDE_TYPES.HERO_3_COLUMN_LAYOUT && (
            <Flex xcss={checkboxStyles}>
              <CheckboxField
                name={`isSearchBarIncluded_${value.id}`}
                defaultIsChecked={value.isSearchBarIncluded}
              >
                {({ fieldProps }) => (
                  <Checkbox
                    {...fieldProps}
                    label="Is Search Bar Included?"
                    onChange={(e) =>
                      onFieldChange('isSearchBarIncluded', value.id)(e.target.checked)
                    }
                  />
                )}
              </CheckboxField>
            </Flex>
          )}
        </Box>
      )}
      {value?.type && value.type !== SLIDE_TYPES.SPOTLIGHT_WITH_RIGHT_IMAGE && (
        <Box xcss={imageBoxStyles}>
          {value?.type === SLIDE_TYPES.HERO_3_COLUMN_LAYOUT && (
            <>
              <ImageField
                label="Left Desktop Image"
                name={`leftBannerImage_${value.id}`}
                customFileName="leftBannerImage"
                uploadFileHandler={uploadStaticAssets}
                isRequired
                url={getImageFieldUrl(value.leftBannerImage)}
                onChange={handleImageFieldChange('leftBannerImage', value.id)}
                enableImageDrop
                imageType="hero"
                isDisabled={isDisabled}
              />
              <ImageField
                label="Right Desktop Image"
                name={`rightBannerImage_${value.id}`}
                customFileName="rightBannerImage"
                uploadFileHandler={uploadStaticAssets}
                isRequired
                url={getImageFieldUrl(value.rightBannerImage)}
                onChange={handleImageFieldChange('rightBannerImage', value.id)}
                enableImageDrop
                imageType="hero"
                isDisabled={isDisabled}
              />
            </>
          )}
          {value?.type === SLIDE_TYPES.HERO_FULL_IMAGE && (
            <ImageField
              label="Desktop Image"
              name={`desktopbannerImage_${value.id}`}
              customFileName="desktopbannerImage"
              uploadFileHandler={uploadStaticAssets}
              isRequired
              url={getImageFieldUrl(value.desktopbannerImage)}
              onChange={handleImageFieldChange('desktopbannerImage', value.id)}
              enableImageDrop
              imageType="hero"
              isDisabled={isDisabled}
            />
          )}

          <ImageField
            label="Tablet Image"
            name={`tabletbannerImage_${value.id}`}
            customFileName="tabletbannerImage"
            uploadFileHandler={uploadStaticAssets}
            isRequired
            url={getImageFieldUrl(value.tabletbannerImage)}
            onChange={handleImageFieldChange('tabletbannerImage', value.id)}
            enableImageDrop
            imageType="hero"
            isDisabled={isDisabled}
          />
          <ImageField
            label="Mobile Image"
            name={`mobilebannerImage_${value.id}`}
            customFileName="mobilebannerImage"
            uploadFileHandler={uploadStaticAssets}
            isRequired
            url={getImageFieldUrl(value.mobilebannerImage)}
            onChange={handleImageFieldChange('mobilebannerImage', value.id)}
            enableImageDrop
            imageType="hero"
            isDisabled={isDisabled}
          />
        </Box>
      )}
    </Flex>
  );
};

export default HPBannerSlideConfig;
